import React, { useState, useEffect } from 'react';
import { X, Clock, User, FileText, ChevronDown, ChevronRight, DollarSign, Users, Target, Settings } from 'lucide-react';
import { multipatrocinadorService } from '../services/multipatrocinadores';

interface HistoryDiffEntry {
  idHistory: number;
  idRegraPromocao: string;
  action: 'INSERT' | 'UPDATE';
  idUser: number;
  userName: string;
  userEmail: string;
  createdAt: string;
  data: any;
  changes: any;
}

interface HistoryDiffModalProps {
  isOpen: boolean;
  onClose: () => void;
  idRegraPromocao: string;
}

export const HistoryDiffModal: React.FC<HistoryDiffModalProps> = ({
  isOpen,
  onClose,
  idRegraPromocao
}) => {
  const [history, setHistory] = useState<HistoryDiffEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (isOpen && idRegraPromocao) {
      loadHistory();
    }
  }, [isOpen, idRegraPromocao]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const response = await multipatrocinadorService.getPromocaoHistoryDiff(idRegraPromocao);

      if (response.success) {
        setHistory(response.data.reverse());
        if (response.data.length > 0) {
          setExpandedEntries(new Set([response.data[response.data.length - 1].idHistory]));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (idHistory: number) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(idHistory)) {
        newSet.delete(idHistory);
      } else {
        newSet.add(idHistory);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: any) => {
    if (!value || value === 0) return 'R$ 0,00';
    const num = parseFloat(value);
    return `R$ ${num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getActionLabel = (action: string) => {
    return action === 'INSERT' ? 'Criação Inicial' : 'Atualização';
  };

  const getActionColor = (action: string) => {
    return action === 'INSERT'
      ? 'bg-green-100 text-green-700 border-green-200'
      : 'bg-blue-100 text-blue-700 border-blue-200';
  };

  const getFieldLabel = (field: string): string => {
    const labels: Record<string, string> = {
      nomePromocao: 'Nome da Promoção',
      budgetPlataforma: 'Budget Plataforma',
      novoBudget: 'Novo Budget',
      confirmarBudget: 'Confirmação de Budget',
      tipoSegmentacao: 'Tipo de Segmentação',
      segmentacao: 'Configuração de Segmentação'
    };
    return labels[field] || field;
  };

  const multipatrocinadorLabels: Record<string, string> = {
    'E-commerce': 'Indústria',
    'Vendedores': 'Distribuidor',
    'Marketplace': 'Marketplace'
  };

  const getCoresMultipatrocinador = (tipo: string) => {
    switch (tipo) {
      case 'E-commerce':
        return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' };
      case 'Vendedores':
        return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' };
      case 'Marketplace':
        return { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };
    }
  };

  const renderMultipatrocinadorConfig = (multipatrocinadores: any) => {
    if (!multipatrocinadores) return null;

    const colunas = ['coluna1', 'coluna2', 'coluna3'];
    return (
      <div className="grid grid-cols-3 gap-3 mt-3">
        {colunas.map((coluna, index) => {
          const config = multipatrocinadores[coluna];
          if (!config) return null;

          const cores = getCoresMultipatrocinador(config.tipo);
          return (
            <div key={coluna} className={`border-2 rounded-lg p-3 ${cores.bg} ${cores.border}`}>
              <div className="text-xs font-semibold text-gray-600 mb-1">Coluna {index + 1}</div>
              <div className={`font-bold text-sm ${cores.text} mb-2`}>
                {multipatrocinadorLabels[config.tipo] || config.tipo}
              </div>
              <div className="flex items-center justify-center">
                <div className={`text-2xl font-bold ${cores.text}`}>
                  {config.porcentagem}%
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSegmentacaoDetalhada = (segmentacao: any) => {
    if (!segmentacao || typeof segmentacao !== 'object') {
      return <span className="text-gray-500 italic">Não configurada</span>;
    }

    // Se tem configurações detalhadas (novo formato)
    if (segmentacao.configuracoes && Array.isArray(segmentacao.configuracoes)) {
      const tipo = segmentacao.tipo || 'N/A';
      const tipoLabels: Record<string, string> = {
        'IDRegra': 'Por ID da Regra',
        'BU': 'Por BU',
        'EAN': 'Por EAN',
        'Marca': 'Por Marca'
      };

      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-300">
            <Target className="h-5 w-5 text-indigo-600" />
            <span className="font-bold text-gray-900">Tipo de Segmentação:</span>
            <span className="font-semibold text-indigo-700">{tipoLabels[tipo] || tipo}</span>
          </div>

          {segmentacao.configuracoes.map((config: any, index: number) => (
            <div key={index} className="bg-gradient-to-br from-gray-50 to-slate-50 border-2 border-gray-300 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-gray-900">{config.nome || `Item ${index + 1}`}</div>
                {config.budget !== undefined && (
                  <div className="flex items-center gap-1 text-sm">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <span className="font-bold text-green-700">{formatCurrency(config.budget)}</span>
                  </div>
                )}
              </div>

              {config.multipatrocinadores && (
                <div>
                  <div className="text-xs font-semibold text-gray-600 mb-2">Distribuição Multipatrocinadores:</div>
                  {renderMultipatrocinadorConfig(config.multipatrocinadores)}
                  <div className="mt-2 text-center">
                    <span className="text-xs font-semibold text-gray-600">
                      Total: {(config.multipatrocinadores.coluna1?.porcentagem || 0) +
                             (config.multipatrocinadores.coluna2?.porcentagem || 0) +
                             (config.multipatrocinadores.coluna3?.porcentagem || 0)}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}

          <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-2 text-center">
            {segmentacao.configuracoes.length} {segmentacao.configuracoes.length === 1 ? 'configuração definida' : 'configurações definidas'}
          </div>
        </div>
      );
    }

    // Formato antigo/resumido
    const items: React.ReactNode[] = [];

    if (segmentacao.idsDistribuidores && Array.isArray(segmentacao.idsDistribuidores)) {
      items.push(
        <div key="dist" className="flex items-center gap-2">
          <Users className="h-4 w-4 text-blue-600" />
          <span className="font-medium">Distribuidores:</span>
          <span className="text-gray-700">{segmentacao.idsDistribuidores.length} selecionados</span>
        </div>
      );
    }

    if (segmentacao.idsBu && Array.isArray(segmentacao.idsBu)) {
      items.push(
        <div key="bu" className="flex items-center gap-2">
          <Target className="h-4 w-4 text-purple-600" />
          <span className="font-medium">BUs:</span>
          <span className="text-gray-700">{segmentacao.idsBu.length} selecionadas</span>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        {items.length > 0 ? items : <span className="text-gray-500 italic">Não configurada</span>}
      </div>
    );
  };

  const renderFieldValue = (field: string, value: any): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">não definido</span>;
    }

    switch (field) {
      case 'budgetPlataforma':
      case 'novoBudget':
        return <span className="font-semibold text-gray-900">{formatCurrency(value)}</span>;

      case 'confirmarBudget':
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
            value ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
          }`}>
            {value ? 'Sim' : 'Não'}
          </span>
        );

      case 'tipoSegmentacao':
        const tipoLabels: Record<string, string> = {
          DISTRIBUIDOR: 'Por Distribuidor',
          BU: 'Por BU',
          EAN: 'Por EAN',
          IDRegra: 'Por ID da Regra',
          Marca: 'Por Marca'
        };
        return <span className="font-medium text-indigo-700">{tipoLabels[value] || value}</span>;

      case 'segmentacao':
        return renderSegmentacaoDetalhada(value);

      case 'nomePromocao':
        return <span className="font-medium text-gray-900">{value}</span>;

      default:
        if (typeof value === 'object') {
          return renderSegmentacaoDetalhada(value);
        }
        return <span className="text-gray-700">{String(value)}</span>;
    }
  };

  const renderInitialState = (data: any) => {
    const fields = [
      { key: 'nomePromocao', icon: FileText, color: 'text-blue-600' },
      { key: 'budgetPlataforma', icon: DollarSign, color: 'text-green-600' },
      { key: 'novoBudget', icon: DollarSign, color: 'text-blue-600' },
      { key: 'confirmarBudget', icon: Settings, color: 'text-purple-600' },
      { key: 'tipoSegmentacao', icon: Target, color: 'text-indigo-600' },
      { key: 'segmentacao', icon: Users, color: 'text-orange-600' }
    ];

    return (
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-green-600 rounded-lg">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <h3 className="text-lg font-bold text-green-900">Estado Inicial da Promoção</h3>
        </div>

        <div className="space-y-4">
          {fields.map(({ key, icon: Icon, color }) => (
            <div key={key} className="bg-white rounded-lg p-4 border border-green-200">
              <div className="flex items-start gap-3">
                <Icon className={`h-5 w-5 ${color} mt-0.5 flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-600 mb-1">{getFieldLabel(key)}</p>
                  <div className="text-base">{renderFieldValue(key, data[key])}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderChanges = (changes: any, isInitial: boolean, data: any) => {
    if (isInitial && changes._initialState) {
      return renderInitialState(changes._initialState);
    }

    const changeKeys = Object.keys(changes).filter(key => key !== '_initialState');
    if (changeKeys.length === 0) {
      return (
        <div className="text-sm text-gray-500 italic text-center py-4">Sem alterações detectadas</div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-amber-600 rounded-lg">
            <Settings className="h-5 w-5 text-white" />
          </div>
          <h3 className="text-lg font-bold text-amber-900">Campos Alterados</h3>
        </div>

        {changeKeys.map((field) => {
          const change = changes[field];

          return (
            <div key={field} className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Target className="h-5 w-5 text-amber-600" />
                <h4 className="font-bold text-amber-900 text-lg">{getFieldLabel(field)}</h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg border-2 border-red-200 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <p className="text-xs font-bold text-red-700 uppercase tracking-wide">Valor Anterior</p>
                  </div>
                  <div className="text-base">{renderFieldValue(field, change.before)}</div>
                </div>

                <div className="bg-white rounded-lg border-2 border-green-200 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <p className="text-xs font-bold text-green-700 uppercase tracking-wide">Valor Atual</p>
                  </div>
                  <div className="text-base">{renderFieldValue(field, change.after)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-600 rounded-lg shadow-lg">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Histórico de Alterações</h2>
              <p className="text-sm text-gray-600">Promoção #{idRegraPromocao}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/50 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>Nenhum histórico encontrado para esta promoção</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry, index) => {
                const isExpanded = expandedEntries.has(entry.idHistory);
                const isInitial = entry.action === 'INSERT';

                return (
                  <div
                    key={entry.idHistory}
                    className={`border-2 rounded-xl overflow-hidden transition-all ${
                      isInitial ? 'border-green-300 bg-green-50/30' : 'border-blue-300 bg-blue-50/30'
                    }`}
                  >
                    <button
                      onClick={() => toggleExpand(entry.idHistory)}
                      className="w-full p-5 flex items-center justify-between hover:bg-white/50 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="h-6 w-6 text-gray-600" />
                          ) : (
                            <ChevronRight className="h-6 w-6 text-gray-600" />
                          )}
                        </div>

                        <div className="flex items-center gap-3 flex-wrap">
                          <span className={`px-4 py-2 rounded-full text-sm font-bold border-2 ${getActionColor(entry.action)}`}>
                            {getActionLabel(entry.action)}
                          </span>
                          <span className="text-base font-bold text-gray-700 bg-white px-3 py-1 rounded-lg border-2 border-gray-200">
                            Versão {history.length - index}
                          </span>
                        </div>

                        <div className="flex items-center gap-6 text-sm text-gray-600 ml-auto">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            <span className="font-medium">{entry.userName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span className="font-medium">{formatDate(entry.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t-2 border-gray-200 p-6 bg-white">
                        {renderChanges(entry.changes, isInitial, entry.data)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gradient-to-r from-gray-50 to-slate-50">
          <p className="text-sm text-gray-600 text-center font-medium">
            {history.length} {history.length === 1 ? 'versão encontrada' : 'versões encontradas'}
          </p>
        </div>
      </div>
    </div>
  );
};
